import React, { useState } from 'react'
import { Alert, Box, Button, CircularProgress } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import PageHeader from '../components/system/PageHeader'
import { SideBarPage } from '../components/system/SidebarPage'
import StageStepper from '../components/theme-ai-tasks/StageStepper'
import DataStage from '../components/theme-ai-tasks/DataStage'
import CodeFrameStage, { SAVE_FRAME_COPY_LABEL } from '../components/theme-ai-tasks/CodeFrameStage'
import NewTaskDialog from '../components/theme-ai-tasks/NewTaskDialog'
import { hasReusableSetup, REUSE_COPY, REUSE_LABEL, reuseSetupFrom } from '../components/theme-ai-tasks/reuseSetup'
import RunStage, { ThemeAIRunOptions } from '../components/theme-ai-tasks/RunStage'
import ResultsStage, { ThemeAILoopedFilter, ThemeAIWriteOutcome } from '../components/theme-ai-tasks/ResultsStage'
import type { UploadSelection } from '../components/theme-ai-tasks/UploadFileStep'
import useAccount from '../hooks/useAccount'
import useAiAccess from '../hooks/useAiAccess'
import useWhoToAsk from '../hooks/useWhoToAsk'
import useRouter from '../hooks/useRouter'
import useThemeAITasks from '../hooks/useThemeAITasks'
import useThemeAIRuns, { isRunInFlight, useThemeAIRunRecords } from '../hooks/useThemeAIRuns'
import useThemeAIScreening, { useThemeAIPersonalDataRecords, useThemeAIScreeningRecords } from '../hooks/useThemeAIScreening'
import useThemeAICodeFrames from '../hooks/useThemeAICodeFrames'
import useThemeAITaskAgent, { AGENT_SAMPLE_SIZES } from '../hooks/useThemeAITaskAgent'
import ThemeAIAgentPane from '../components/theme-ai-tasks/ThemeAIAgentPane'
import { GenNode } from '../types/carbon/models/GenNode'
import type { ThemeAIDataSource, ThemeAIResponseScope, ThemeAITask } from '../types/gotypes'
import {
  deriveThemeAIStageProgress,
  hasReadyThemeAIData,
  isThemeAIRunCurrent,
  resolveThemeAIStageParam,
  THEME_AI_STAGE_ORDER,
  ThemeAIRunView,
  ThemeAITaskStage,
} from '../utils/themeAIStageProgress'
import { recommendedSampleSize } from '../utils/themeAIDistribution'
import { defaultWriteVariableName, isThemeAIJobVariableSourced, writtenFromResponse } from '../utils/themeAIWriteback'
import { downloadThemeAIResultsExcel } from '../utils/themeAIResultsExcel'
import { themeAIReaderUnit } from '../utils/themeAICost'
import { tableRef } from '../utils/outputStamp'
import { themeAIWriteDecision } from '../utils/themeAIOwnership'
import { effectiveScreening, JUNK_RUNNING_REASON, junkCheckDetails } from '../utils/themeAIJunk'

const RECORDS_PAGE_SIZE = 25
const PREVIEW_SIZE = 8

// Task detail controller: owns every hook and hands each stage plain data and
// callbacks. Durable task and run records decide where the user is, so a
// reload resumes at the next action.
const ThemeAITaskView: React.FC = () => {
  const account = useAccount()
  // A Manager's estimate is our rate-card cost in pounds; anyone else's is a price in the customer's unit.
  const isManager = Boolean(account.user?.role_permissions?.isManager)
  const router = useRouter()

  const customer = router.params.customer || account.customer?.name || ''
  // AI access (#1537): the code-frame agent and a test on a sample need level
  // 2, a full run level 4. Below that the control is greyed with the reason;
  // opening a task and reading results stay open. The server refuses too.
  const aiAccess = useAiAccess(customer)
  const exploreBlockedReason = aiAccess.can('themeai_explore') ? undefined : aiAccess.refusalFor('themeai_explore')
  const runBlockedReason = aiAccess.can('themeai_run') ? undefined : aiAccess.refusalFor('themeai_run')
  const job = router.params.job || account.job?.name || ''
  const taskId = router.params.taskId as string
  // Who to ask about a task or frame this person cannot change (Adam, 8 Oct).
  const whoToAsk = useWhoToAsk(customer)
  const viewer = { email: account.user?.session_info?.email, isManager }

  const tasks = useThemeAITasks({ customer, job, taskId, previewSize: PREVIEW_SIZE })
  const task = tasks.task

  const [selectedRunNumber, setSelectedRunNumber] = useState<number>()
  const [sampleSize, setSampleSize] = useState<number>()
  // scope stays unset until the user picks one; the runs hook then chooses
  // (responses without codes after a run, else everything).
  const [runOptions, setRunOptions] = useState<Omit<ThemeAIRunOptions, 'scope'> & { scope?: ThemeAIResponseScope }>({
    includeExplanations: false,
    includeSentiment: false,
    includeEmotion: false,
  })
  const [selectedThemeCode, setSelectedThemeCode] = useState<string>()
  const [selectedLooped, setSelectedLooped] = useState<ThemeAILoopedFilter>()
  // Results start with the steps panel closed, to give the results the room
  // (Matt and Adam, 24 Sep, #1523 item 5); a toggle holds for that step.
  const [sidebarChoice, setSidebarChoice] = useState<{ stage: ThemeAITaskStage; open: boolean }>()
  const [recordsOffset, setRecordsOffset] = useState(0)
  const [runError, setRunError] = useState<string>()
  const [studyContextDraft, setStudyContextDraft] = useState<string>()
  const [reuseOpen, setReuseOpen] = useState(false)
  // The junk review's filter and page (#1891).
  const [junkCategory, setJunkCategory] = useState<string>()
  const [junkOffset, setJunkOffset] = useState(0)
  const [junkConfirmOpen, setJunkConfirmOpen] = useState(false)
  // The personal data review's filter and page (#494).
  const [personalCategory, setPersonalCategory] = useState<string>()
  const [personalOffset, setPersonalOffset] = useState(0)

  const codeFrames = useThemeAICodeFrames({ customer, job, frameId: task?.code_frame_id, taskId })
  // SKY-20 (#1234): a pass the customer has switched off is never asked for.
  const includeSentiment = runOptions.includeSentiment && !codeFrames.standardFrames?.sentiment_off
  const includeEmotion = runOptions.includeEmotion && !codeFrames.standardFrames?.emotion_off
  const frameVersion = codeFrames.codeFrame?.versions?.find((version) => version.version === task?.code_frame_version)
    || codeFrames.codeFrame?.versions?.[codeFrames.codeFrame.versions.length - 1]
  const frameThemes = task?.code_frame_id ? frameVersion?.themes || [] : []
  const poolSize = task?.pool_size ?? task?.data_source?.upload?.row_count ?? 0

  const runs = useThemeAIRuns({
    customer,
    job,
    taskId,
    task,
    resultsRunNumber: selectedRunNumber,
    estimate: {
      enabled: Boolean(task?.code_frame_id && hasReadyThemeAIData(task) && frameThemes.length > 0),
      responseScope: runOptions.scope,
      includeExplanations: runOptions.includeExplanations,
      includeSentiment,
      includeEmotion,
      themeCount: frameThemes.length,
      responseCount: poolSize,
    },
  })
  const agent = useThemeAITaskAgent({
    customer,
    job,
    task,
    frameThemes,
    frameGroups: frameVersion?.groups,
    frameIsStandard: Boolean(codeFrames.codeFrame?.standard),
    loadTaskSample: tasks.loadTaskSample,
    updateTask: tasks.updateTask,
    saveThemes: codeFrames.handleSaveThemes,
  })

  const progress = deriveThemeAIStageProgress(task, runs.runs)
  // `?stage=test` opens Run with its test view: Test is a question inside Run
  // now, not a step (#1492).
  const requested = resolveThemeAIStageParam(router.params.stage)
  const currentStage: ThemeAITaskStage = requested && progress.reachable.includes(requested.stage) ? requested.stage : progress.next
  const runView: ThemeAIRunView = currentStage === 'run' && requested?.view ? requested.view : 'run'

  // The check's estimate is read only while its confirmation is open.
  const junk = useThemeAIScreening({ customer, job, taskId, task, estimateEnabled: junkConfirmOpen })
  const junkRecords = useThemeAIScreeningRecords(customer, job, taskId, effectiveScreening(junk.screening)?.id, {
    category: junkCategory,
    limit: RECORDS_PAGE_SIZE,
    offset: junkOffset,
    enabled: runView === 'junk',
  })
  const personalRecords = useThemeAIPersonalDataRecords(customer, job, taskId, effectiveScreening(junk.screening)?.id, {
    category: personalCategory,
    limit: RECORDS_PAGE_SIZE,
    offset: personalOffset,
    enabled: runView === 'personal-data',
  })

  const testRun = runs.latestCurrentTestRun
  const testing = runs.createTestRunLoading || (testRun ? isRunInFlight(testRun) : runs.testRunPending)
  const resultsRun = runs.resultsRun
  const recordsRunNumber =
    currentStage === 'run' && runView === 'test' && testRun?.status === 'completed' ? testRun.run_number
      : currentStage === 'results' && resultsRun?.status === 'completed' ? resultsRun.run_number
        : undefined
  const records = useThemeAIRunRecords(customer, job, taskId, recordsRunNumber, {
    theme: selectedThemeCode,
    sentiment: selectedLooped?.kind === 'sentiment' ? selectedLooped.code : undefined,
    emotion: selectedLooped?.kind === 'emotion' ? selectedLooped.code : undefined,
    limit: RECORDS_PAGE_SIZE,
    offset: recordsOffset,
  })

  const clearSelection = () => {
    setSelectedThemeCode(undefined)
    setSelectedLooped(undefined)
    setRecordsOffset(0)
  }

  const handleSelectStage = (stage: ThemeAITaskStage) => {
    clearSelection()
    router.setParams({ stage })
  }

  const handleRunViewChange = (view: ThemeAIRunView) => {
    clearSelection()
    setJunkCategory(undefined)
    setJunkOffset(0)
    setPersonalCategory(undefined)
    setPersonalOffset(0)
    router.setParams({ stage: view })
  }

  // One filter at a time: a theme, or a sentiment or emotion code.
  const handleSelectTheme = (code: string | undefined) => {
    setSelectedThemeCode(code)
    setSelectedLooped(undefined)
    setRecordsOffset(0)
  }

  const handleSelectLooped = (filter: ThemeAILoopedFilter | undefined) => {
    setSelectedLooped(filter)
    setSelectedThemeCode(undefined)
    setRecordsOffset(0)
  }

  const handleSelectRun = (runNumber: number) => {
    clearSelection()
    setSelectedRunNumber(runNumber)
  }

  const handleJunkCategoryChange = (category: string | undefined) => {
    setJunkCategory(category)
    setJunkOffset(0)
  }

  // A failed save is reported by the hook; the Keep switch is put back by its refetch.
  const handleSetJunkKept = (recordId: string, kept: boolean) => {
    junk.handleSetKept(recordId, kept).catch(() => undefined)
  }

  const handlePersonalCategoryChange = (category: string | undefined) => {
    setPersonalCategory(category)
    setPersonalOffset(0)
  }

  const handleSetPersonalKept = (recordId: string, kept: boolean) => {
    junk.handleSetPersonalDataKept(recordId, kept).catch(() => undefined)
  }

  const afterDataReplaced = () => {
    agent.handleReset()
    setJunkCategory(undefined)
    setJunkOffset(0)
    setPersonalCategory(undefined)
    setPersonalOffset(0)
    setSelectedRunNumber(undefined)
    setSampleSize(undefined)
    handleSelectStage('code_frame')
  }

  const handleReplaceWithFile = async ({ file, columns }: UploadSelection) => {
    if (!task) return
    await tasks.uploadTaskData(task.id, file, columns)
    afterDataReplaced()
  }

  const handleReplaceWithVariable = async (node: GenNode) => {
    if (!task) return
    await tasks.updateTask(task.id, {
      data_source: { type: 'job_variable', job_variable: { job, variable_name: node.value1 || '', question: node.value2 || '' } },
    })
    afterDataReplaced()
  }

  const handleSaveStudyContext = async () => {
    if (!task || studyContextDraft === undefined) return
    await tasks.updateTask(task.id, { study_context: studyContextDraft.trim() })
    setStudyContextDraft(undefined)
  }

  const handleChooseFrame = async (frameId: string, version: number) => {
    if (!task) return
    await tasks.updateTask(task.id, { code_frame_id: frameId, code_frame_version: version })
  }

  const handleUploadFrame = async (file: File) => {
    await codeFrames.handleImportCodeFrame(file)
  }

  const handleSaveCopyOfFrame = async () => {
    const frame = codeFrames.codeFrame
    if (!frame) return
    await codeFrames.handleSaveCopyOfFrame(frame.id, `${frame.name} (copy)`)
  }

  const handleRunTest = async (size?: number) => {
    clearSelection()
    if (size !== undefined) setSampleSize(size)
    try {
      await runs.handleCreateTestRun(size ?? sampleSize ?? recommendedSampleSize(poolSize), { agentSampleSize: agent.sampleSize })
    } catch {
      // The hook keeps the error for the stage to show.
    }
  }

  const handleStartRun = async () => {
    if (!task) return
    setRunError(undefined)
    try {
      const run = await runs.handleStartRun({
        response_scope: runs.estimateScope,
        code_frame_id: task.code_frame_id,
        code_frame_version: task.code_frame_version,
        include_explanations: runOptions.includeExplanations,
        include_sentiment: includeSentiment,
        include_emotion: includeEmotion,
        response_count: runs.estimate?.responseCount,
        token_estimate: runs.estimate?.estimatedTokens,
      })
      setSelectedRunNumber(run?.run_number)
      handleSelectStage('results')
    } catch (error) {
      setRunError(error instanceof Error ? error.message : 'The run could not be started.')
      throw error
    }
  }

  const handleResumePendingRun = async () => {
    setRunError(undefined)
    const runNumber = runs.pendingRun?.run_number
    try {
      await runs.handleResumePendingRun()
      setSelectedRunNumber(runNumber)
      handleSelectStage('results')
    } catch (error) {
      setRunError(error instanceof Error ? error.message : 'The run could not be started.')
    }
  }

  const handleDiscardPendingRun = async () => {
    setRunError(undefined)
    try {
      await runs.handleDiscardPendingRun()
    } catch {
      // Reported by the hook.
    }
  }

  // "Use this set-up on other data": a new task of the user's own with this
  // task's frame and context. It opens as soon as it exists, as from the list.
  const handleReuse = async (name: string, dataSource: ThemeAIDataSource, upload?: UploadSelection) => {
    if (!task) return
    let created: ThemeAITask
    try {
      created = await tasks.createTask({ name, data_source: dataSource, ...reuseSetupFrom(task) })
    } catch {
      return
    }
    if (upload) {
      try {
        await tasks.uploadTaskData(created.id, upload.file, upload.columns)
      } catch {
        // Reported by the hook; the copy opens on its Data step to retry.
      }
    }
    setReuseOpen(false)
    router.navigate('theme-ai-task', { customer, job, taskId: created.id, stage: 'data' })
  }

  const handleWriteVariable = async (variableName: string, overwrite: boolean): Promise<ThemeAIWriteOutcome> => {
    const result = await runs.handleWriteVariable({ variable_name: variableName, overwrite })
    if (result.status === 'conflict') return { status: 'conflict' }
    return { status: 'written', ...writtenFromResponse(result.data) }
  }

  const handleDownloadExcel = () => {
    const results = runs.results?.available ? runs.results.results : undefined
    if (!results || !task || !resultsRun) return
    downloadThemeAIResultsExcel(results, isThemeAIRunCurrent(task, resultsRun) ? frameThemes : undefined, `${task.name} - run ${resultsRun.run_number}.xlsx`, {
      who: account.user?.session_info?.email || account.user?.session_info?.name || '',
      when: new Date(),
      ref: tableRef(job, `ThemeAI/${task.name} run ${resultsRun.run_number}`),
    })
  }

  if (tasks.taskLoading || runs.runsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (tasks.taskError || !task) {
    return <Alert severity="error">This Theme AI task could not be loaded.</Alert>
  }

  // Every write to the task mirrors canModifyThemeAITask: its owner, or a
  // Manager on a Company-level task. Anyone else sees those controls disabled
  // with this reason, and Reuse makes them a task of their own.
  const reusable = hasReusableSetup(task)
  const taskWriteBlockedReason = themeAIWriteDecision(task, viewer, "You can't change this task.", whoToAsk, reusable ? REUSE_COPY : undefined).reason
  // A frame is edited in place by the same rule (canModifyThemeAICodeFrame);
  // anyone else saves a copy. A standard frame is copied by everyone.
  const frame = codeFrames.codeFrame
  const frameEditBlockedReason = task.code_frame_id && frame && !frame.standard
    ? themeAIWriteDecision(frame, viewer, "You can't edit this frame.", whoToAsk, `“${SAVE_FRAME_COPY_LABEL}”`).reason
    : undefined

  const latestRun = runs.latestCurrentRun
  const source = task.data_source
  const frameName = codeFrames.codeFrame?.name
  const codedRuns = runs.currentRuns.filter((run) => !run.is_test && run.status === 'completed')
  // Once anything on this data has been coded, whatever frame it used, the
  // Run stage becomes "Run again" (spec #916 §6, scenarios A–D).
  const lastCodedRun = runs.runs.find((run) => !run.is_test && run.status === 'completed' && run.data_revision === task.data_revision)
  const details: Partial<Record<ThemeAITaskStage, string>> = {
    data: source
      ? `${source.type === 'upload' ? source.upload?.file_name : source.job_variable?.variable_name} · ${poolSize.toLocaleString()} responses`
      : undefined,
    code_frame: frameThemes.length > 0 ? `${frameName || 'Code frame'} · ${frameThemes.length} themes` : undefined,
    run: latestRun
      ? `Run #${latestRun.run_number} · ${latestRun.status}`
      : testRun?.status === 'completed' ? `Tested on ${testRun.response_count.toLocaleString()} sampled` : undefined,
    results: latestRun?.status === 'completed' ? `${latestRun.response_count.toLocaleString()} coded` : undefined,
  }

  return (
    <SideBarPage
      title="Steps"
      sidebarOpen={sidebarChoice?.stage === currentStage ? sidebarChoice.open : currentStage !== 'results'}
      onSidebarOpenChange={(open) => setSidebarChoice({ stage: currentStage, open })}
      sidebar={<StageStepper current={currentStage} stages={progress.stages} details={details} onSelect={handleSelectStage} />}
      collapsedContent={<StageStepper compact current={currentStage} stages={progress.stages} details={details} onSelect={handleSelectStage} />}
    >
    <Box sx={{ height: '100%', overflowY: 'auto' }}>
    <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
      {/* The task, then the step: nothing else is said twice. The agent opens
          from the Code frame step, where it works (Matt, 24 Sep, #1523 items
          7 and 8). */}
      <PageHeader
        title={task.name}
        subtitle={THEME_AI_STAGE_ORDER.find((stage) => stage.key === currentStage)?.label}
        actions={reusable ? (
          <Button color="info" startIcon={<ContentCopyIcon />} onClick={() => setReuseOpen(true)}>
            {REUSE_LABEL}
          </Button>
        ) : undefined}
      />
      {reusable && (
        <NewTaskDialog
          open={reuseOpen}
          reuseFrom={task}
          customer={customer}
          job={job}
          creating={tasks.createTaskLoading || tasks.uploadTaskDataLoading}
          onClose={() => setReuseOpen(false)}
          onInspectUpload={tasks.inspectUpload}
          onCreate={handleReuse}
        />
      )}
      <ThemeAIAgentPane
        open={agent.open}
        onClose={agent.handleClose}
        messages={agent.messages}
        loading={agent.loading || tasks.taskSampleLoading}
        error={agent.error}
        hasFrame={frameThemes.length > 0}
        frameName={frameName}
        frameThemeNames={frameThemes.map((theme) => theme.name)}
        frameVersion={task.code_frame_version}
        proposedThemes={agent.proposedThemes}
        onThemesChange={agent.setProposedThemes}
        saving={codeFrames.createCodeFrameLoading || codeFrames.createVersionLoading || tasks.updateTaskLoading}
        sampleSize={agent.sampleSize}
        sampleSizes={AGENT_SAMPLE_SIZES}
        poolSize={poolSize}
        onSampleSizeChange={agent.setSampleSize}
        onSend={agent.handleSend}
        onSave={agent.handleSaveProposedThemes}
      />

      {currentStage === 'data' && (
        <DataStage
          customer={customer}
          job={job}
          source={source}
          poolSize={poolSize}
          previewRows={tasks.preview?.rows || []}
          previewLoading={tasks.previewLoading}
          replacing={tasks.updateTaskLoading || tasks.uploadTaskDataLoading}
          canContinue={progress.reachable.includes('code_frame')}
          studyContext={task.study_context || ''}
          studyContextDraft={studyContextDraft}
          savingStudyContext={tasks.updateTaskLoading}
          onStudyContextDraftChange={setStudyContextDraft}
          onSaveStudyContext={handleSaveStudyContext}
          onInspectUpload={tasks.inspectUpload}
          onReplaceWithFile={handleReplaceWithFile}
          onReplaceWithVariable={handleReplaceWithVariable}
          onFetchResponses={tasks.fetchTaskData}
          fetchingResponses={tasks.fetchTaskDataLoading}
          jobContext={tasks.preview?.job_context}
          onOpenJobContext={() => router.navigate('admin-context', { customer, job })}
          onContinue={() => handleSelectStage('code_frame')}
          writeBlockedReason={taskWriteBlockedReason}
        />
      )}

      {currentStage === 'code_frame' && (
        <CodeFrameStage
          frameName={frameName}
          frameVersion={task.code_frame_version}
          frameThemes={frameThemes}
          frameGroups={frameVersion?.groups}
          frameLoading={Boolean(task.code_frame_id) && codeFrames.codeFrameLoading}
          frameStandard={codeFrames.codeFrame?.standard}
          library={codeFrames.codeFrames}
          libraryLoading={codeFrames.codeFramesLoading}
          saving={codeFrames.createCodeFrameLoading || codeFrames.createVersionLoading || codeFrames.saveCopyLoading || tasks.updateTaskLoading}
          importing={codeFrames.importCodeFrameLoading}
          canPromote={Boolean(account.user?.role_permissions?.isManager)}
          onPromoteFrame={codeFrames.handlePromoteCodeFrame}
          onDemoteFrame={codeFrames.handleDemoteCodeFrame}
          onSaveCopy={handleSaveCopyOfFrame}
          onOpenAgent={agent.handleOpen}
          agentBlockedReason={taskWriteBlockedReason ?? frameEditBlockedReason ?? exploreBlockedReason}
          writeBlockedReason={taskWriteBlockedReason}
          frameEditBlockedReason={frameEditBlockedReason}
          onSaveThemes={agent.handleSaveThemes}
          onChooseFrame={handleChooseFrame}
          onUploadFrame={handleUploadFrame}
          onContinue={() => handleSelectStage('run')}
          hasStudyContext={Boolean(task.study_context?.trim())}
          onEditStudyContext={() => handleSelectStage('data')}
        />
      )}

      {currentStage === 'run' && (
        <RunStage
          poolSize={poolSize}
          frameName={frameName}
          frameVersion={task.code_frame_version}
          themeCount={frameThemes.length}
          lastRun={lastCodedRun}
          uncodedCount={runs.uncodedCount}
          options={{ ...runOptions, scope: runs.estimateScope }}
          onOptionsChange={setRunOptions}
          estimate={runs.estimate ? { ...runs.estimate, themeCount: frameThemes.length, remainingBudget: runs.estimate.remainingBudget ?? undefined, moneyUnit: themeAIReaderUnit(isManager, runs.estimate.moneyUnit), serverEstimateAvailable: !runs.estimate.isFallback } : undefined}
          estimateLoading={runs.estimateLoading}
          estimateError={runs.estimate?.isFallback ? 'The server estimate is unavailable, so this is an approximate figure.' : undefined}
          pendingRun={runs.pendingRun}
          runningRun={latestRun && latestRun.status === 'running' ? latestRun : undefined}
          starting={runs.startRunLoading || runs.resumePendingRunLoading || runs.discardPendingRunLoading}
          startError={runError}
          onStartRun={handleStartRun}
          onResumePendingRun={handleResumePendingRun}
          onDiscardPendingRun={handleDiscardPendingRun}
          onViewResults={() => handleSelectStage('results')}
          standardFrames={codeFrames.standardFrames}
          view={runView}
          onViewChange={handleRunViewChange}
          runBlockedReason={taskWriteBlockedReason ?? runBlockedReason}
          writeBlockedReason={taskWriteBlockedReason}
          junk={{
            screening: junk.screening,
            estimate: junk.estimate ? junkCheckDetails(junk.estimate, isManager) : undefined,
            estimateLoading: junk.estimateLoading,
            starting: junk.startCheckLoading || junk.discardLoading || junk.setDecisionLoading || junk.setPersonalDataDecisionLoading,
            blockedReason: taskWriteBlockedReason ?? exploreBlockedReason,
            writeBlockedReason: taskWriteBlockedReason,
            onConfirmOpenChange: setJunkConfirmOpen,
            onStartCheck: junk.handleStartCheck,
            onDiscard: junk.handleDiscard,
            records: {
              records: junkRecords.records,
              total: junkRecords.recordsTotal,
              offset: junkOffset,
              pageSize: RECORDS_PAGE_SIZE,
              loading: junkRecords.recordsFetching,
              onPageChange: setJunkOffset,
            },
            category: junkCategory,
            onCategoryChange: handleJunkCategoryChange,
            onSetDecision: junk.handleSetDecision,
            onSetKept: handleSetJunkKept,
          }}
          personalData={{
            records: {
              records: personalRecords.records,
              total: personalRecords.recordsTotal,
              offset: personalOffset,
              pageSize: RECORDS_PAGE_SIZE,
              loading: personalRecords.recordsFetching,
              onPageChange: setPersonalOffset,
            },
            category: personalCategory,
            onCategoryChange: handlePersonalCategoryChange,
            onSetDecision: junk.handleSetPersonalDataDecision,
            onSetKept: handleSetPersonalKept,
            onDownload: junk.handleDownloadPersonalData,
            downloading: junk.downloadPersonalDataLoading,
          }}
          test={{
            poolSize,
            sampleSize: sampleSize ?? recommendedSampleSize(poolSize),
            onSampleSizeChange: setSampleSize,
            frameThemes,
            testRun,
            results: runs.testResults,
            resultsLoading: runs.testResultsLoading,
            testing,
            error: runs.createTestRunError,
            records: {
              records: records.records,
              total: records.recordsTotal,
              offset: recordsOffset,
              pageSize: RECORDS_PAGE_SIZE,
              loading: records.recordsFetching,
              onPageChange: setRecordsOffset,
            },
            selectedThemeCode,
            onSelectTheme: handleSelectTheme,
            onRunTest: handleRunTest,
            onRefineFrame: () => handleSelectStage('code_frame'),
            freshSample: runs.testFreshSample,
            blockedReason: taskWriteBlockedReason ?? exploreBlockedReason ?? (junk.screeningInFlight ? JUNK_RUNNING_REASON : undefined),
          }}
        />
      )}

      {currentStage === 'results' && (
        <ResultsStage
          runs={runs.runs}
          activeRun={resultsRun}
          activeRunStale={Boolean(resultsRun && !isThemeAIRunCurrent(task, resultsRun))}
          onSelectRun={handleSelectRun}
          results={runs.results?.available ? runs.results.results : undefined}
          resultsLoading={runs.resultsLoading}
          resultsError={runs.resultsError ? 'The results could not be loaded.' : undefined}
          frameThemes={frameThemes}
          records={{
            records: records.records,
            total: records.recordsTotal,
            offset: recordsOffset,
            pageSize: RECORDS_PAGE_SIZE,
            loading: records.recordsFetching,
            onPageChange: setRecordsOffset,
          }}
          selectedThemeCode={selectedThemeCode}
          onSelectTheme={handleSelectTheme}
          selectedLooped={selectedLooped}
          onSelectLooped={handleSelectLooped}
          onDownloadCsv={runs.downloadRunCsv}
          onDownloadExcel={handleDownloadExcel}
          uploadingCorrections={runs.uploadCorrectionsLoading}
          onUploadCorrections={async (file) => { await runs.uploadCorrections(file) }}
          writeBack={{
            available: isThemeAIJobVariableSourced(task),
            enabled: runs.runs.some((run) => !run.is_test && run.status === 'completed'),
            warning: codedRuns.length === 0
              ? 'Some responses were coded with an earlier code frame or data source.'
              : undefined,
            defaultVariableName: defaultWriteVariableName(source?.job_variable?.variable_name),
            writing: runs.writeVariableLoading,
            onWrite: handleWriteVariable,
            onOpenTable: (written) => router.navigate('tables', written.table
              ? { customer: written.customer, job: written.job, table: written.table }
              : { customer: written.customer, job: written.job }),
          }}
          onGoToRun={() => handleSelectStage('run')}
          writeBlockedReason={taskWriteBlockedReason}
        />
      )}
    </Box>
    </Box>
    </SideBarPage>
  )
}

// Keyed on the task, so opening another one (Reuse opens its new task) starts
// from fresh page state rather than the last task's selections and drafts.
const ThemeAITaskPage: React.FC = () => {
  const router = useRouter()
  return <ThemeAITaskView key={router.params.taskId} />
}

export default ThemeAITaskPage
